import { BrowserRouter } from 'react-router-dom';
import './App.css';
import './css/QuillFonts.css';
import { useEffect } from "react";
import { auth, db } from './firebase';
import { doc, getDoc } from "firebase/firestore";
import Main from "./Main"
import { QuickLinksProvider } from './contexts/QuickLinksContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { trySSOSignIn, clearSSOCode } from './utils/sso';
// import Quill from "quill";
// import QuillTableBetter from "quill-table-better";
// import "quill-better-table/dist/quill-better-table.css";
// import "react-quill-new/dist/quill.snow.css";
// import "react-quill/dist/quill.snow.css";
// Quill.register({ 'modules/better-table': QuillTableBetter }, true);
const syncEmailWithBackend = async () => {
        try {
            const user = auth.currentUser;
            if (user) {
                        const token = await user.getIdToken(); // Get Firebase authentication token
                        const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/users/update/email/", {
                                method: "POST",
                                headers: {
                                        "Content-Type": "application/json",
                                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,  
                                },
                        });
                        const data = await response.json();
                }
        } catch (err) {
                console.error("Error syncing email:", err);
        }
};

const ensureBackendUserExists = async () => {
        try {
                const user = auth.currentUser;
                if (!user) return;
                
                const token = await user.getIdToken();
                
                // First, check if user exists in backend
                const checkResponse = await fetch(
                        `https://bluebirddocumentationadmin.pythonanywhere.com/users/${user.uid}/`,
                        {
                                headers: {
                                        "Authorization": `${token}`
                                }
                        }
                );

                const userData = await checkResponse.json();
                
                if (checkResponse.ok && userData.firebase_uid && userData.firebase_uid.trim() !== "") {
                        // User exists in backend, all good
                        return;
                }
                
                if (checkResponse.status === 404 || (checkResponse.ok && (!userData.firebase_uid || userData.firebase_uid.trim() === ""))) {
                        
                        // Try to get name from Firestore
                        let name = "";
                        try {
                                const firestoreUserDoc = await getDoc(doc(db, "users", user.uid));
                                if (firestoreUserDoc.exists()) {
                                        name = firestoreUserDoc.data()?.name || "";
                                }
                        } catch (firestoreError) {
                                console.warn("Could not fetch name from Firestore:", firestoreError);
                        }
                        
                        // Call the ensure-exists endpoint
                        const createResponse = await fetch(
                                "https://bluebirddocumentationadmin.pythonanywhere.com/users/ensure-exists/",
                                {
                                        method: "POST",
                                        headers: {
                                                "Content-Type": "application/json",
                                                "Authorization": `${token}`,
                                        },
                                        body: JSON.stringify({
                                                email: user.email,
                                                name: name,
                                                userId: user.uid,
                                        })
                                }
                        );
                        
                        if (createResponse.ok) {
                                const result = await createResponse.json();
                        } else {
                                const errorData = await createResponse.json();
                                console.error("Auto-repair failed:", errorData);
                                // Could add retry logic here or show modal after multiple failures
                        }
                } else {
                        // Some other error occurred
                        console.error("Error checking backend user existence:", checkResponse.status);
                }
        } catch (err) {
                console.error("Error in ensureBackendUserExists:", err);
                // Silently fail - don't disrupt user experience
        }
};

const checkEmailVerification = async (user) => {
        try {
                // Force refresh to get latest verification status
                await user.reload();

                // Check if email is verified
                if (!user.emailVerified) {
                        // Only redirect if not already on verification or allowed pages
                        const currentPath = window.location.pathname;
                        const allowedPaths = [
                                '/verify-email',
                                '/account',
                                '/login',
                                '/register',
                                '/reset',
                                '/privacy-policy'
                        ];
                        
                        const isOnAllowedPath = allowedPaths.some(path => currentPath.startsWith(path));
                        
                        if (!isOnAllowedPath) {
                                // Store the intended destination
                                localStorage.setItem('redirectAfterVerification', currentPath);
                                // Redirect to verification page
                                window.location.href = '/verify-email';
                        }
                }
        } catch (error) {
                console.error("Error checking email verification:", error);
        }
};

const prefetchWorkspaceData = async (user) => {
        try {
                if (!user) return;
                
                const token = await user.getIdToken();
                
                // Fetch workspaces list
                const response = await fetch('https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/', {
                        method: "GET",
                        headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `${token}`,
                                'Auth-Token': `Bearer ${token}`,
                                'Requesting-User-Uid': user.uid
                        }
                });
                
                if (!response.ok) return;
                
                const workspaces = await response.json();
                if (!workspaces || workspaces.length === 0) return;

                const ownedWorkspaces = workspaces.filter(workspace => workspace.role === "OWNER");
                if (ownedWorkspaces.length === 0) return;

                // Store all owned workspace IDs for HomePage to use
                sessionStorage.setItem(
                        'prefetchedWorkspaceIds',
                        JSON.stringify(ownedWorkspaces.map(w => w.id))
                );
                // Keep singular keys populated with the first owned workspace
                // for backward compatibility with consumers that still read them.
                sessionStorage.setItem('prefetchedWorkspaceId', ownedWorkspaces[0].id);
                sessionStorage.setItem('prefetchedWorkspaceRole', ownedWorkspaces[0].role);
        } catch (error) {
                // Silently fail - this is just optimization
                console.debug("Workspace prefetch skipped:", error);
        }
}

function App() {
        useEffect(() => {
                const unsubscribe = auth.onAuthStateChanged(async (user) => {
                    if (user) {
                        // Ensure backend user exists first (other calls depend on user existing)
                        await ensureBackendUserExists();
                        // Run remaining init calls in parallel — they are independent
                        await Promise.all([
                            syncEmailWithBackend(),
                            checkEmailVerification(user),
                        ]);
                        // Prefetch workspace data in background (non-blocking)
                        prefetchWorkspaceData(user);
                    } else {
                        clearSSOCode();
                        // See if another suite app just handed us a sign-in code before
                        // rendering the logged-out state (BBD-50).
                        await trySSOSignIn(); // if successful, onAuthStateChanged fires again
                    }
                });
        
                return () => unsubscribe(); // Cleanup listener when component unmounts
            }, []);

        return (
                <BrowserRouter>
                        <ThemeProvider>
                                <QuickLinksProvider>
                                        <Main />
                                </QuickLinksProvider>
                        </ThemeProvider>
                </BrowserRouter>
        )
}

export default App;